import { HttpErrorResponse } from "@angular/common/http";
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, Input, OnInit } from "@angular/core";
import { HierarchyNode } from "d3-hierarchy";
import { EMPTY, Observable, catchError, from, take, takeUntil, zip } from "rxjs";
import dayjs from "@gtmhub/dayjs";
import { EmployeeService, IEmployeeDetails } from "@gtmhub/employees";
import { UIErrorHandlingService } from "@gtmhub/error-handling";
import { IGoal, IGoalNode } from "@gtmhub/goals/models";
import { GoalsTreeService } from "@gtmhub/goals/services/goals-tree.service";
import { bySessionEndDateDesc, createSessionTree } from "@gtmhub/sessions/controllers/utils";
import { PlanningSessionsActions } from "@gtmhub/sessions/redux/session-actions";
import { ISessionsStoreState } from "@gtmhub/sessions/redux/session-reducer";
import { reduxStoreContainer } from "@gtmhub/state-management/state-management.module";
import { IReduxTeam, ITeam, ITeamsStoreState, TeamsActions } from "@gtmhub/teams";
import { getCurrentUserId } from "@gtmhub/users";
import { IdMap, toIdMap } from "@gtmhub/util";
import { BaseListModel } from "@webapp/core/abstracts/models/base-list.model";
import { RequestPaging } from "@webapp/core/abstracts/models/request.paging";
import { ICollection } from "@webapp/core/core.models";
import { ReduxStoreObserver } from "@webapp/core/state-management/redux-store-observer";
import { DestroyService } from "@webapp/core/util/services/destroy.service";
import { createErrorObject } from "@webapp/error-handling/error-util";
import { MoveOkrModalData } from "@webapp/okrs/components/clone-move-okrs/move-okrs/move-okr-modal.component";
import { Goal } from "@webapp/okrs/goals/models/goal.models";
import { GoalsFacade } from "@webapp/okrs/goals/services/goals-facade.service";
import { ISearchCondition, ISearchField, SearchOperatorsEnum } from "@webapp/search/models/search-api.models";
import { Search, SearchDTO } from "@webapp/search/models/search.models";
import { SearchFacade } from "@webapp/search/services/search-facade.service";
import { IPlanningSessionsIdMap, PlanningSessionStatus, Session } from "@webapp/sessions/models/sessions.model";
import { UiModalRef } from "@webapp/ui/modal/abstracts/modal-ref";
import { UI_MODAL_DATA } from "@webapp/ui/modal/modal.models";
import { GoalTreeService } from "../goals/goal-tree/goal-tree.service";
import { GoalSelectorGoalItems, SingleGoalSelection, SingleGoalSelectorView } from "../single-goal-selector.types";

export type SingleGoalSelectorModalComponentData = {
  unselectableId: string;
  enableOkrSelectionOnly: boolean;
};

const goalFields = ["parentId,assignee,description,name,sessionId,obfuscated,private,parentGoalSessionId,ownerIds,attainmentTypeString"];

@Component({
  selector: "single-goal-selector-modal",
  templateUrl: "./single-goal-selector.component.html",
  styleUrls: ["./single-goal-selector.component.less"],
  providers: [DestroyService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SingleGoalSelectorModalComponent implements OnInit {
  @Input() public unselectableId: string;
  @Input() public enableOkrSelectionOnly: boolean;

  public filterQuery: string;
  public goals: IGoalNode;
  public goalItems: GoalSelectorGoalItems;
  public indicators: {
    gettingGoals: boolean;
    loading: boolean;
  };
  private ownerInfo: {
    id: string;
    teamIds: string[];
    parentTeamIds: string[];
  };

  public selectedSession: Session;
  public selectedScreen: SingleGoalSelectorView;
  public selection: SingleGoalSelection;
  public sessions: Session[];
  public sessionsTree: HierarchyNode<Session>;
  private sessionsMap: IPlanningSessionsIdMap;
  private teamsIdMap: IdMap<IReduxTeam>;

  constructor(
    private employeeService: EmployeeService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private goalsFacade: GoalsFacade,
    private goalsTreeService: GoalsTreeService,
    private modalRef: UiModalRef,
    private planningSessionsActions: PlanningSessionsActions,
    private searchFacade: SearchFacade,
    private teamsActions: TeamsActions,
    private goalTreeService: GoalTreeService,
    private destroy$: DestroyService,
    private cdRef: ChangeDetectorRef,
    @Inject(UI_MODAL_DATA) modalData: MoveOkrModalData
  ) {
    this.indicators = { loading: true, gettingGoals: false };
    this.sessionsMap = {};
    this.goalItems = {
      myGoals: [],
      myTeamsGoals: [],
      myParentTeamsGoals: [],
    };
    this.ownerInfo = {
      id: getCurrentUserId(),
      teamIds: [],
      parentTeamIds: [],
    };

    this.selectedScreen = "sessions";
    this.filterQuery = "";
    Object.assign(this, modalData);
  }

  get hasSessions(): boolean {
    return this.sessionsMap && Object.keys(this.sessionsMap).length > 0;
  }

  private get sessionsIds(): string[] {
    return this.sessionsMap ? Object.keys(this.sessionsMap) : [];
  }

  public ngOnInit(): void {
    const { reduxStore } = reduxStoreContainer;

    reduxStore.dispatch(this.planningSessionsActions.getSessionsIfMissing());
    reduxStore.dispatch(this.teamsActions.fetchTeamsIfMissing());

    const reduxObserver = new ReduxStoreObserver(reduxStore);
    reduxObserver
      .whenFetched$<ISessionsStoreState & ITeamsStoreState>("sessions", "teams")
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => {
        this.setStates(state);
        this.loadData();
      });

    this.goalTreeService
      .getSelectedGoal$()
      .pipe(takeUntil(this.destroy$))
      .subscribe((goal: IGoal) => {
        this.onGoalSelected(goal);
        this.goalTreeService.setSelectedGoal(null);
      });
  }

  private setStates(state: ISessionsStoreState & ITeamsStoreState): void {
    const isActionAllowedForThisSession = (session: Session): boolean => session.status === PlanningSessionStatus.OPEN;
    const allowedSessions = state.sessions.items.filter((s) => isActionAllowedForThisSession(s) && !s.demo);
    const sessions = allowedSessions.filter((s) => dayjs().isSameOrBefore(s.end));
    this.sessions = sessions;
    this.sessionsTree = this.buildSessionsTree(sessions);
    this.sessionsMap = toIdMap(sessions);
    this.teamsIdMap = state.teams.map;
  }

  private buildSessionsTree(sessions: Session[]): HierarchyNode<Session> {
    if (!sessions || sessions.length === 0) {
      return null;
    }

    return createSessionTree(sessions).sort(bySessionEndDateDesc);
  }

  public backToSessions(): void {
    this.changeView("sessions");
    this.goals = {
      goal: {},
      children: [],
    };
    this.selectedSession = {};
    this.filterQuery = "";
  }

  private changeView(selectedView: SingleGoalSelectorView): void {
    this.selectedScreen = selectedView;
  }

  public filterByName(filterQuery: string): void {
    if (this.selectedScreen === "sessions") {
      this.selectedSession = {};
    }

    if (!filterQuery?.length && !this.selectedSession?.id) {
      this.changeView("sessions");
    } else {
      this.changeView("goals");
      const sessionIds = this.selectedSession?.id ? [this.selectedSession.id] : this.sessionsIds;
      this.loadGoals(sessionIds, filterQuery);
    }
  }

  private formatGoalsSearchResponse(item: Search<"goals">): IGoal {
    const parentId = item.fields.parent?.goalId || item.fields.parent?.metricId;
    // Obfuscated is not part of the search api response, it’s something v2 api calculates.
    // The search api returns private goals/metrics that the user making the request owns, that’s why it’s set explicitly to false here
    return { id: item.id, parentId: parentId, obfuscated: false, ...item.fields };
  }

  private loadGoals(sessionIds: string[], filterQuery = ""): void {
    this.indicators.gettingGoals = true;
    const searchBody = this.getSearchBody(sessionIds, filterQuery, "goals");
    this.searchFacade
      .searchData$<"goals">(searchBody)
      .pipe(
        take(1),
        catchError((error: HttpErrorResponse) => {
          this.uiErrorHandlingService.handleModal(createErrorObject(error));
          return EMPTY;
        })
      )
      .subscribe((result) => {
        this.setGoals(result);
      });
  }

  private setGoals(result: ICollection<Search<"goals">>): void {
    const goals = result.items.map(this.formatGoalsSearchResponse);
    if (result.items.length) {
      this.goals = this.goalsTreeService.constructGoalsTree(result.items[0].fields.sessionId, goals, [{ fieldName: "name", reversed: false }]);
    } else {
      this.goals = {
        goal: {},
        children: [],
      };
    }

    this.indicators.gettingGoals = false;
    this.cdRef.detectChanges();
  }

  private loadData(): void {
    from(this.employeeService.getEmployee(this.ownerInfo.id))
      .pipe(
        take(1),
        catchError((error: HttpErrorResponse) => {
          this.uiErrorHandlingService.handleModal(createErrorObject(error));
          return EMPTY;
        })
      )
      .subscribe((employee) => {
        this.ownerInfo.teamIds = this.getMyTeamsIds(employee);
        this.getAllGoals();
      });
  }

  private getAllGoals(): void {
    const myGoals: Observable<BaseListModel<Goal>> = this.goalsFacade.getAll$(this.generateMongoParams(this.sessionsIds, [this.ownerInfo.id]));
    const getGoals: Observable<BaseListModel<Goal>>[] = [myGoals];
    if (this.ownerInfo.teamIds.length) {
      const myTeamsGoals: Observable<BaseListModel<Goal>> = this.goalsFacade.getAll$(this.generateMongoParams(this.sessionsIds, this.ownerInfo.teamIds));
      getGoals.push(myTeamsGoals);

      const teams = (this.ownerInfo?.teamIds || []).map((teamId) => this.teamsIdMap[teamId]);
      this.ownerInfo.parentTeamIds = this.getParentTeamsIds(teams);
      if (this.ownerInfo.parentTeamIds.length) {
        const myParentTeamsGoals: Observable<BaseListModel<Goal>> = this.goalsFacade.getAll$(this.generateMongoParams(this.sessionsIds, this.ownerInfo.parentTeamIds));
        getGoals.push(myParentTeamsGoals);
      }
    }

    zip(getGoals)
      .pipe(
        take(1),
        catchError((error: HttpErrorResponse) => {
          this.uiErrorHandlingService.handleModal(createErrorObject(error));
          this.indicators.gettingGoals = false;
          return EMPTY;
        })
      )
      .subscribe(([myGoals, myTeamsGoals, myParentTeamsGoals]) => {
        this.goalItems.myGoals = myGoals?.items || [];
        this.goalItems.myTeamsGoals = myTeamsGoals?.items || [];
        this.goalItems.myParentTeamsGoals = myParentTeamsGoals?.items || [];
        this.indicators.loading = false;
        this.cdRef.detectChanges();
      });
  }

  private getParentTeamsIds(teams: ITeam[]): string[] {
    return (teams || []).map((team) => team?.parentId).filter((teamId, index, self) => teamId && self.indexOf(teamId) === index);
  }

  private getMyTeamsIds(employee: IEmployeeDetails): string[] {
    return employee.info.managedTeamIds.concat(employee.info.teamIds).filter((teamId, index, self) => self.indexOf(teamId) === index);
  }

  public onGoalSelected(goal: IGoal): void {
    this.modalRef.destroy({ type: "goal", goal });
  }

  public onSessionSelected(session: Session): void {
    this.modalRef.destroy({ type: "session", session });
  }

  public selectSessionFromList(session: Session): void {
    this.selectedScreen = "goals";
    this.selectedSession = session;
    this.filterQuery = "";
    this.loadGoals([session.id]);
  }

  private generateMongoParams(sessionIds: string[], ownerIds: string[]): RequestPaging {
    return {
      ...new RequestPaging(),
      filter: { sessionId: { $in: sessionIds }, ownerIds: { $in: ownerIds } },
      fields: goalFields,
      take: -1,
    };
  }

  private getSearchBody(sessionIds: string[], filterQuery: string, collectionName: string): SearchDTO {
    const searchConditions: ISearchCondition[] = [
      {
        fieldName: "sessionId",
        fieldCondition: {
          operator: "in",
          value: sessionIds,
        },
      },
    ];

    const searchFields: ISearchField[] = filterQuery
      ? [
          {
            name: "name",
          },
          {
            name: "description",
          },
          {
            name: "assignees.name",
          },
        ]
      : [];

    return {
      searchTerm: filterQuery,
      operator: filterQuery ? SearchOperatorsEnum.matchPrefix : SearchOperatorsEnum.matchAny,
      searchRequests: [
        {
          collectionName: collectionName,
          searchFields: searchFields,
          searchConditions: searchConditions,
        },
      ],
    };
  }
}
